"use client";

import Image from "next/image";
import {
  H3,
  P2,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@focal/ui-kit";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDeviceOS } from "@/hooks/useDeviceOS";
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL } from "@/constants";

interface AppDownloadPromptProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AppDownloadPrompt({
  open,
  onOpenChange,
}: AppDownloadPromptProps) {
  const { isMobile, isTablet } = useIsMobile();
  const { isIOS, isAndroid, isMobileDevice } = useDeviceOS();

  const handleContinueOnWeb = () => {
    onOpenChange?.(false);
  };

  // Determine QR code size based on screen size
  const qrCodeSize = isTablet ? 110 : 140;
  const badgeWidth = 120;
  const badgeHeight = 40;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          isMobile
            ? "max-w-sm w-[calc(100%-32px)] rounded-xl"
            : "w-[425px] max-w-[425px]"
        }
        showCloseIcon={false}
        data-cy="app-download-prompt"
      >
        <DialogHeader className="text-center items-center">
          <DialogTitle asChild>
            <H3 spacing="none" data-cy="app-download-prompt-title">
              Download the Focal mobile app
            </H3>
          </DialogTitle>
          <DialogDescription asChild>
            <P2 textColor="secondary" className="mt-1">
              For the best experience, download the mobile app for in-person
              meetings.
            </P2>
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="py-4">
          {isMobileDevice ? (
            // Mobile view: Show app store badge images in blue card
            <div
              className="bg-secondary rounded-xl p-6 w-full"
              data-cy="app-download-mobile-buttons"
            >
              <div className="flex flex-col gap-3 items-center">
                <P2 className="font-medium text-center">
                  Click & download app
                </P2>
                {isIOS ? (
                  <a
                    href={IOS_APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                    data-cy="download-ios-button"
                  >
                    <Image
                      src="/onboarding/app-store1.svg"
                      alt="Download on the App Store"
                      width={badgeWidth}
                      height={badgeHeight}
                      priority
                    />
                  </a>
                ) : isAndroid ? (
                  <a
                    href={ANDROID_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                    data-cy="download-android-button"
                  >
                    <Image
                      src="/onboarding/google-play1.svg"
                      alt="Get it on Google Play"
                      width={badgeWidth + 15}
                      height={badgeHeight}
                      priority
                    />
                  </a>
                ) : (
                  // Unknown mobile device - show both
                  <>
                    <a
                      href={IOS_APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-80 transition-opacity"
                      data-cy="download-ios-button"
                    >
                      <Image
                        src="/onboarding/app-store1.svg"
                        alt="Download on the App Store"
                        width={badgeWidth}
                        height={badgeHeight}
                        priority
                      />
                    </a>
                    <a
                      href={ANDROID_PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-80 transition-opacity"
                      data-cy="download-android-button"
                    >
                      <Image
                        src="/onboarding/google-play1.svg"
                        alt="Get it on Google Play"
                        width={badgeWidth + 15}
                        height={badgeHeight}
                        priority
                      />
                    </a>
                  </>
                )}
              </div>
            </div>
          ) : (
            // Desktop view: Show QR codes in a blue card
            <div
              className="bg-secondary rounded-xl p-6"
              data-cy="app-download-qr-codes"
            >
              <div className="flex items-start justify-center gap-6">
                {/* iOS QR code */}
                <div className="flex flex-col items-center text-center gap-1">
                  <div
                    className="bg-white rounded-lg flex items-center justify-center"
                    style={{ width: qrCodeSize, height: qrCodeSize }}
                  >
                    <Image
                      src="/onboarding/ios.png"
                      alt="Scan to download Focal for iOS"
                      width={qrCodeSize - 16}
                      height={qrCodeSize - 16}
                      priority
                    />
                  </div>
                  <P2 className="font-medium mt-1">Scan for iOS</P2>
                  <a
                    href={IOS_APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    <Image
                      src="/onboarding/app-store1.svg"
                      alt="Download on the App Store"
                      width={badgeWidth}
                      height={badgeHeight}
                      priority
                    />
                  </a>
                </div>

                {/* Android QR code */}
                <div className="flex flex-col items-center text-center gap-1">
                  <div
                    className="bg-white rounded-lg flex items-center justify-center"
                    style={{ width: qrCodeSize, height: qrCodeSize }}
                  >
                    <Image
                      src="/onboarding/android.png"
                      alt="Scan to download Focal for Android"
                      width={qrCodeSize - 16}
                      height={qrCodeSize - 16}
                      priority
                    />
                  </div>
                  <P2 className="font-medium mt-1">Scan for Android</P2>
                  <a
                    href={ANDROID_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    <Image
                      src="/onboarding/google-play1.svg"
                      alt="Get it on Google Play"
                      width={badgeWidth + 15}
                      height={badgeHeight}
                      priority
                    />
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter className="justify-center sm:justify-center">
          <Button
            variant="outline"
            onClick={handleContinueOnWeb}
            data-cy="continue-on-web-button"
            className="self-center"
          >
            Continue in web browser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
